import { useRef, useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Share2 } from "lucide-react";
import { getDayHadith } from "@/data/dailyHadiths";

interface ProgressData {
  prayer: number;
  quran: number;
  fasting: number;
  dhikr: number;
  deeds: number;
}

interface ShareProgressCardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentDay: number;
  streak: number;
  progress: ProgressData;
  userName?: string;
}

const CANVAS_W = 1080;
const CANVAS_H = 1920;

const GOLD = "#e2b15b";
const GOLD_DIM = "rgba(226, 177, 91, 0.25)";
const GOLD_FAINT = "rgba(226, 177, 91, 0.10)";
const GREEN_DARK = "#0a2e1f";
const GREEN_MID = "#144a33";
const GREEN_LIGHT = "#1a5e3f";

const TRACKERS = [
  { key: "prayer" as const, label: "Salah", emoji: "🕌", color: "#2d9b6e" },
  { key: "quran" as const, label: "Quran", emoji: "📖", color: "#e2b15b" },
  { key: "fasting" as const, label: "Fasting", emoji: "🌙", color: "#8b5ec8" },
  { key: "dhikr" as const, label: "Dhikr", emoji: "📿", color: "#3b9ea5" },
  { key: "deeds" as const, label: "Deeds", emoji: "💚", color: "#d45e7a" },
];

// Draw interlocking circles pattern — carved depth effect matching landing page
function drawIslamicPattern(ctx: CanvasRenderingContext2D, w: number, h: number) {
  const patW = 180;
  const patH = 300;
  const r = 90;

  ctx.save();

  // Grid of circle centers (matching IslamicPattern.tsx layout)
  const centers: [number, number][] = [];
  for (let gx = -patW; gx < w + patW * 2; gx += patW) {
    for (let gy = -patH; gy < h + patH; gy += patH / 4) {
      centers.push([gx, gy]);
      centers.push([gx + patW / 2, gy]);
    }
  }

  // Draw each circle with carved depth (shadow + highlight layers)
  centers.forEach(([cx, cy]) => {
    const dx = (cx - w / 2) / (w / 2);
    const dy = (cy - h / 2) / (h / 2);
    const dist = Math.sqrt(dx * dx + dy * dy);
    const alpha = Math.max(0, 0.14 - dist * 0.06);
    if (alpha <= 0) return;

    // Shadow layer (bottom-right offset) — carved inset effect
    ctx.strokeStyle = `rgba(5, 30, 18, ${alpha * 2.5})`;
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    ctx.arc(cx + 0.8, cy + 0.8, r, 0, Math.PI * 2);
    ctx.stroke();

    // Highlight layer (top-left offset) — light source
    ctx.strokeStyle = `rgba(150, 190, 160, ${alpha * 0.7})`;
    ctx.lineWidth = 1.0;
    ctx.beginPath();
    ctx.arc(cx - 0.5, cy - 0.5, r, 0, Math.PI * 2);
    ctx.stroke();

    // Main circle stroke
    ctx.strokeStyle = `rgba(150, 180, 155, ${alpha})`;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();
  });

  // Vertical lines with same carved effect
  for (let x = 0; x < w; x += patW) {
    const dx = Math.abs((x - w / 2) / (w / 2));
    const alpha = Math.max(0, 0.08 - dx * 0.04);
    if (alpha <= 0) continue;

    [x, x + patW / 2].forEach((lx) => {
      ctx.strokeStyle = `rgba(5, 30, 18, ${alpha * 2})`;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(lx + 0.6, 0);
      ctx.lineTo(lx + 0.6, h);
      ctx.stroke();

      ctx.strokeStyle = `rgba(150, 190, 160, ${alpha * 0.5})`;
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(lx - 0.4, 0);
      ctx.lineTo(lx - 0.4, h);
      ctx.stroke();

      ctx.strokeStyle = `rgba(150, 180, 155, ${alpha})`;
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(lx, 0);
      ctx.lineTo(lx, h);
      ctx.stroke();
    });
  }

  ctx.restore();
}

// Thin outlined crescent moon — matching the landing page style
function drawCrescentMoon(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number) {
  // Subtle glow behind
  const glow = ctx.createRadialGradient(cx, cy, size * 0.2, cx, cy, size * 1.8);
  glow.addColorStop(0, "rgba(226, 177, 91, 0.10)");
  glow.addColorStop(1, "rgba(226, 177, 91, 0)");
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(cx, cy, size * 1.8, 0, Math.PI * 2);
  ctx.fill();

  // Draw crescent as a stroke path (not filled)
  ctx.save();
  ctx.strokeStyle = GOLD;
  ctx.lineWidth = 3;

  // Create crescent by clipping: draw outer arc, then cut with inner arc
  // We'll draw it manually as two arcs forming the crescent shape
  ctx.beginPath();
  // Outer circle arc
  const outerR = size;
  const innerR = size * 0.82;
  const offsetX = size * 0.35;
  const offsetY = -size * 0.15;

  // Calculate intersection points of two circles to draw just the crescent
  // Outer circle: center (cx, cy), radius outerR
  // Inner circle: center (cx + offsetX, cy + offsetY), radius innerR
  const d = Math.sqrt(offsetX * offsetX + offsetY * offsetY);
  const a = (outerR * outerR - innerR * innerR + d * d) / (2 * d);
  const hh = Math.sqrt(Math.max(0, outerR * outerR - a * a));
  const angle = Math.atan2(offsetY, offsetX);

  const midX = cx + a * Math.cos(angle);
  const midY = cy + a * Math.sin(angle);
  const perpAngle = angle + Math.PI / 2;

  const ix1 = midX + hh * Math.cos(perpAngle);
  const iy1 = midY + hh * Math.sin(perpAngle);
  const ix2 = midX - hh * Math.cos(perpAngle);
  const iy2 = midY - hh * Math.sin(perpAngle);

  // Outer arc (the visible crescent edge)
  const startAngleOuter = Math.atan2(iy1 - cy, ix1 - cx);
  const endAngleOuter = Math.atan2(iy2 - cy, ix2 - cx);

  ctx.beginPath();
  ctx.arc(cx, cy, outerR, endAngleOuter, startAngleOuter);

  // Inner arc (the cut-in edge) — go the other way
  const startAngleInner = Math.atan2(iy1 - (cy + offsetY), ix1 - (cx + offsetX));
  const endAngleInner = Math.atan2(iy2 - (cy + offsetY), ix2 - (cx + offsetX));
  ctx.arc(cx + offsetX, cy + offsetY, innerR, startAngleInner, endAngleInner, true);

  ctx.closePath();
  ctx.stroke();
  ctx.restore();
}

// Wrap text to fit within maxWidth, returns array of lines
function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(" ");
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth) {
      if (current) lines.push(current);
      current = word;
    } else {
      current = test;
    }
  }
  if (current) lines.push(current);
  return lines;
}

function drawCard(
  ctx: CanvasRenderingContext2D,
  { currentDay, streak, progress, userName }: Omit<ShareProgressCardProps, "open" | "onOpenChange">
) {
  const w = CANVAS_W;
  const h = CANVAS_H;

  // Background gradient
  const bg = ctx.createLinearGradient(0, 0, 0, h);
  bg.addColorStop(0, GREEN_DARK);
  bg.addColorStop(0.3, GREEN_MID);
  bg.addColorStop(0.7, GREEN_LIGHT);
  bg.addColorStop(1, GREEN_DARK);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, w, h);

  // Islamic geometric pattern overlay
  drawIslamicPattern(ctx, w, h);

  // Border frames
  const inset = 48;
  ctx.strokeStyle = GOLD_DIM;
  ctx.lineWidth = 3;
  roundRectStroke(ctx, inset, inset, w - inset * 2, h - inset * 2, 12);
  ctx.strokeStyle = GOLD_FAINT;
  ctx.lineWidth = 1.5;
  roundRectStroke(ctx, inset + 14, inset + 14, w - (inset + 14) * 2, h - (inset + 14) * 2, 8);

  // Corner ornaments
  const cs = 70;
  ctx.strokeStyle = `rgba(226, 177, 91, 0.45)`;
  ctx.lineWidth = 3;
  [[inset, inset, 1, 1], [w - inset, inset, -1, 1], [inset, h - inset, 1, -1], [w - inset, h - inset, -1, -1]].forEach(
    ([cx, cy, dx, dy]) => {
      ctx.beginPath();
      ctx.moveTo(cx, cy + dy * cs);
      ctx.lineTo(cx, cy);
      ctx.lineTo(cx + dx * cs, cy);
      ctx.stroke();
      // Inner L
      ctx.strokeStyle = `rgba(226, 177, 91, 0.2)`;
      ctx.beginPath();
      ctx.moveTo(cx + dx * 8, cy + dy * (cs - 10));
      ctx.lineTo(cx + dx * 8, cy + dy * 8);
      ctx.lineTo(cx + dx * (cs - 10), cy + dy * 8);
      ctx.stroke();
      ctx.strokeStyle = `rgba(226, 177, 91, 0.45)`;
    }
  );

  // Crescent moon
  drawCrescentMoon(ctx, w / 2, 190, 55);

  // "RAMADAN" letter-spaced header
  ctx.fillStyle = GOLD;
  ctx.font = "600 34px system-ui, -apple-system, sans-serif";
  ctx.textAlign = "center";
  ctx.letterSpacing = "12px";
  ctx.fillText("R A M A D A N", w / 2, 320);
  ctx.letterSpacing = "0px";

  // Day number — large display
  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 110px system-ui, -apple-system, sans-serif";
  ctx.fillText(`Day ${currentDay}`, w / 2, 445);

  ctx.fillStyle = "rgba(255, 255, 255, 0.45)";
  ctx.font = "300 30px system-ui, -apple-system, sans-serif";
  ctx.fillText("of 30", w / 2, 490);

  // Username
  if (userName) {
    ctx.fillStyle = "rgba(226, 177, 91, 0.6)";
    ctx.font = "500 26px system-ui, -apple-system, sans-serif";
    ctx.fillText(userName, w / 2, 535);
  }

  // Divider line
  const divY = 570;
  ctx.strokeStyle = GOLD_DIM;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(w * 0.25, divY);
  ctx.lineTo(w * 0.75, divY);
  ctx.stroke();
  // Diamond center
  ctx.fillStyle = GOLD;
  ctx.save();
  ctx.translate(w / 2, divY);
  ctx.rotate(Math.PI / 4);
  ctx.fillRect(-5, -5, 10, 10);
  ctx.restore();

  // Overall progress ring
  const overall = TRACKERS.reduce((sum, t) => sum + progress[t.key], 0) / TRACKERS.length;
  const ringX = w / 2;
  const ringY = 700;
  const ringR = 120;

  // Ring background
  ctx.strokeStyle = "rgba(255, 255, 255, 0.08)";
  ctx.lineWidth = 18;
  ctx.beginPath();
  ctx.arc(ringX, ringY, ringR, 0, Math.PI * 2);
  ctx.stroke();

  // Ring progress arc
  const ringGrad = ctx.createLinearGradient(ringX - ringR, ringY - ringR, ringX + ringR, ringY + ringR);
  ringGrad.addColorStop(0, "#e2b15b");
  ringGrad.addColorStop(1, "#f0d48a");
  ctx.strokeStyle = ringGrad;
  ctx.lineWidth = 18;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.arc(ringX, ringY, ringR, -Math.PI / 2, -Math.PI / 2 + (Math.PI * 2 * overall) / 100);
  ctx.stroke();
  ctx.lineCap = "butt";

  // Percentage inside ring
  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 64px system-ui, -apple-system, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText(`${Math.round(overall)}%`, ringX, ringY + 18);

  ctx.fillStyle = "rgba(255, 255, 255, 0.5)";
  ctx.font = "300 22px system-ui, -apple-system, sans-serif";
  ctx.fillText("Overall Progress", ringX, ringY + 55);

  // Tracker bars
  const barStartY = 890;
  const barSpacing = 88;
  const barLeft = 150;
  const barRight = w - 150;
  const barWidth = barRight - barLeft;

  TRACKERS.forEach((tracker, i) => {
    const y = barStartY + i * barSpacing;
    const val = progress[tracker.key];

    // Emoji + Label
    ctx.fillStyle = "rgba(255, 255, 255, 0.65)";
    ctx.font = "400 26px system-ui, -apple-system, sans-serif";
    ctx.textAlign = "left";
    ctx.fillText(`${tracker.emoji}  ${tracker.label}`, barLeft, y);

    // Percentage
    ctx.textAlign = "right";
    ctx.fillStyle = "#ffffff";
    ctx.font = "600 26px system-ui, -apple-system, sans-serif";
    ctx.fillText(`${Math.round(val)}%`, barRight, y);

    // Bar bg
    const barY = y + 12;
    const barH = 14;
    ctx.fillStyle = "rgba(255, 255, 255, 0.08)";
    roundRect(ctx, barLeft, barY, barWidth, barH, 7);

    // Bar fill
    if (val > 0) {
      const barGrad = ctx.createLinearGradient(barLeft, barY, barLeft + barWidth * (val / 100), barY);
      barGrad.addColorStop(0, tracker.color);
      barGrad.addColorStop(1, tracker.color + "cc");
      ctx.fillStyle = barGrad;
      roundRect(ctx, barLeft, barY, Math.max(barWidth * (val / 100), barH), barH, 7);
    }
  });

  // Streak badge
  const streakY = barStartY + TRACKERS.length * barSpacing + 60;
  if (streak > 0) {
    ctx.fillStyle = "rgba(226, 177, 91, 0.12)";
    roundRect(ctx, w / 2 - 160, streakY - 35, 320, 70, 35);
    ctx.strokeStyle = GOLD_DIM;
    ctx.lineWidth = 1.5;
    roundRectStroke(ctx, w / 2 - 160, streakY - 35, 320, 70, 35);

    ctx.fillStyle = "#ffffff";
    ctx.font = "600 36px system-ui, -apple-system, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(`🔥 ${streak} day streak`, w / 2, streakY + 8);
  }

  // Hadith of the day section
  const hadith = getDayHadith(currentDay);
  if (hadith) {
    const hadithStartY = streak > 0 ? streakY + 70 : streakY - 10;

    // Section divider
    ctx.strokeStyle = GOLD_FAINT;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(w * 0.2, hadithStartY);
    ctx.lineTo(w * 0.8, hadithStartY);
    ctx.stroke();

    // "Hadith of the Day" label
    ctx.fillStyle = GOLD;
    ctx.font = "500 22px system-ui, -apple-system, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("— Hadith of the Day —", w / 2, hadithStartY + 40);

    // Arabic text (centered)
    ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
    ctx.font = "32px system-ui, -apple-system, sans-serif";
    ctx.textAlign = "center";
    ctx.direction = "rtl";

    const arabicLines = wrapText(ctx, hadith.arabic, w - 240);
    let arabicY = hadithStartY + 85;
    arabicLines.forEach((line) => {
      ctx.fillText(line, w / 2, arabicY);
      arabicY += 42;
    });
    ctx.direction = "ltr";

    // Translation (English, italic feel)
    ctx.fillStyle = "rgba(255, 255, 255, 0.55)";
    ctx.font = "italic 24px system-ui, -apple-system, sans-serif";
    const translationLines = wrapText(ctx, `"${hadith.translation}"`, w - 260);
    let transY = arabicY + 12;
    translationLines.forEach((line) => {
      ctx.fillText(line, w / 2, transY);
      transY += 32;
    });

    // Source
    ctx.fillStyle = "rgba(226, 177, 91, 0.5)";
    ctx.font = "400 20px system-ui, -apple-system, sans-serif";
    ctx.fillText(hadith.source, w / 2, transY + 10);
  }

  // Branding at bottom — matching landing page Playfair Display style
  ctx.fillStyle = GOLD;
  ctx.font = "italic 700 48px 'Playfair Display', Georgia, serif";
  ctx.textAlign = "center";
  ctx.fillText("Layali", w / 2, h - 130);

  ctx.fillStyle = "rgba(255, 255, 255, 0.4)";
  ctx.font = "300 20px system-ui, -apple-system, sans-serif";
  ctx.letterSpacing = "3px";
  ctx.fillText("P O W E R E D   B Y", w / 2, h - 95);
  ctx.letterSpacing = "0px";

  ctx.fillStyle = "rgba(226, 177, 91, 0.6)";
  ctx.font = "italic 500 22px 'Playfair Display', Georgia, serif";
  ctx.fillText("Dua and Affirmations", w / 2, h - 65);
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.fill();
}

function roundRectStroke(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.stroke();
}

export const ShareProgressCard = ({
  open,
  onOpenChange,
  currentDay,
  streak,
  progress,
  userName,
}: ShareProgressCardProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [canShare, setCanShare] = useState(false);

  useEffect(() => {
    setCanShare(typeof navigator.share === "function");
  }, []);

  useEffect(() => {
    if (!open) return;

    const frameId = requestAnimationFrame(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      canvas.width = CANVAS_W;
      canvas.height = CANVAS_H;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      drawCard(ctx, { currentDay, streak, progress, userName });
      setPreviewUrl(canvas.toDataURL("image/png"));
    });

    return () => cancelAnimationFrame(frameId);
  }, [open, currentDay, streak, progress, userName]);

  const handleDownload = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `ramadan-day-${currentDay}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  }, [currentDay]);

  const handleShare = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], `ramadan-day-${currentDay}.png`, { type: "image/png" });
      try {
        await navigator.share({ files: [file], title: `Ramadan Day ${currentDay} Progress` });
      } catch {
        // user cancelled
      }
    }, "image/png");
  }, [currentDay]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm p-4 bg-card">
        <DialogTitle className="text-center text-sm font-medium text-muted-foreground">
          Share Your Progress
        </DialogTitle>

        <canvas ref={canvasRef} className="hidden" />

        {previewUrl && (
          <img
            src={previewUrl}
            alt="Progress card preview"
            className="w-full rounded-lg shadow-md"
          />
        )}

        <div className="flex gap-2 pt-2">
          <Button onClick={handleDownload} className="flex-1 gap-2" variant="outline">
            <Download className="w-4 h-4" />
            Download
          </Button>
          {canShare && (
            <Button onClick={handleShare} className="flex-1 gap-2">
              <Share2 className="w-4 h-4" />
              Share
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
